import { select, Store } from '@ngxs/store';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Animations } from '../../angular-animations/angular-animations';
import { UserAccessService } from '../../services/user-access.service';
import { MenuListLogicService } from '../../services/menu-list-logic.service';
import { MenuStateModel } from '../../state/menu-list.state';
import { ClearFilterDetails } from '../../state/main-table-filter.actions';
import { ScreenSizeService } from '../../services/screen-size.service';
import { SidenavService } from '../../services/side-nav.service';
import { MainMenuChange } from '../../state/menu-tree-structure.actions';
import { ModuleNumbers } from '../../enums/module-numbers.enum';
import { RouteLoadingTrackerService } from '../../services/route-loading-tracker.service';
import { isModulePaused } from 'src/app/shared/user-view/functions/is-module-paused';
import {
  ALL_DEPS_TOKEN,
  TenantUserAccessState,
} from 'src/app/tenant-selector/state/tenant-user-access.state';

@Component({
  selector: 'app-menu-list-recursive',
  templateUrl: './menu-list-recursive.component.html',
  styleUrls: ['./menu-list-recursive.component.scss'],
  animations: [Animations.toggleContainer, Animations.rotatedState],
  standalone: false,
})
export class MenuListRecursiveComponent implements OnChanges, OnInit {
  @Input() navItems: any[];
  @Input() currentTenantId: string;
  @Input() currentMenuState: MenuStateModel;
  @Input() isInAdmin: boolean;
  @Input() dueListWarningsExpiredSum: number;
  @Input() dueListWarningsExpiringSum: number;
  @Input() currentlyAvailableModules: ModuleNumbers[];

  expandedItems: string[] = [];
  activeRoute: string;
  activeNavItem: any;
  currentDepartment = select(TenantUserAccessState.currentDepartment);
  readonly dashboardRoute = '/';

  constructor(
    private userAccessService: UserAccessService,
    private router: Router,
    private store: Store,
    private screenSizeService: ScreenSizeService,
    private sidenavService: SidenavService,
    private routeLoadingTracker: RouteLoadingTrackerService,
    private menuListLogicService: MenuListLogicService
  ) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        [this.activeRoute] = event.urlAfterRedirects.split('?');
        this.checkActiveNavItem();
      }
    });
  }

  ngOnInit(): void {
    [this.activeRoute] = this.router.url.split('?');
    this.filterNavItemsByAvailableModules();
    this.checkActiveNavItem();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.navItems) {
      this.filterNavItemsByAvailableModules();
      this.checkActiveNavItem();
    }
  }

  checkActiveNavItem(): void {
    this.activeNavItem = this.navItems.find((item) => this.hasActiveChildren(item.children));
  }

  hasActiveChildren(children: any[]): boolean {
    return children?.some((child) => this.activeRoute === child.route);
  }

  isVisible(role: string): boolean {
    return this.userAccessService.hasAccess(role);
  }

  toggleItem(name: string): void {
    if (this.isInAdmin) {
      this.store.dispatch(new ClearFilterDetails());
    } else if (this.isExpanded(name)) {
      this.expandedItems = this.expandedItems.filter((itemName: string) => itemName !== name);
    } else {
      this.expandedItems.push(name);
    }
    this.store.dispatch(new MainMenuChange(name));
    this.store.dispatch(new ClearFilterDetails());
    // we close the sidenav on screens with width below 1200px
    this.closeSidenav(name);
  }

  closeSidenav(name?: string) {
    if (this.screenSizeService.isMobileSubject.value && name !== 'adminSettings') {
      this.sidenavService.toggle();
    }
  }

  isMenuItemLoading(menuItemName: string): boolean {
    return this.routeLoadingTracker.isMenuItemLoading(menuItemName);
  }

  isModulePausedForNavItem(navItem: { moduleNumber?: ModuleNumbers; route?: string }): boolean {
    const department = this.currentDepartment();
    if (!department || department.id === ALL_DEPS_TOKEN) {
      return false;
    }

    const moduleNumber =
      navItem.moduleNumber ?? this.menuListLogicService.getModuleNumberFromUrl(navItem.route ?? '');
    if (moduleNumber == null) {
      return false;
    }

    return isModulePaused(department, moduleNumber);
  }

  private isExpanded(name: string): boolean {
    return this.expandedItems.some((itemName: string) => itemName === name);
  }

  private filterNavItemsByAvailableModules(): void {
    this.navItems = this.navItems.filter((item) => {
      // If it's a WorkAndRest module, check if it exists in currentlyAvailableModules
      if (item.moduleNumber === ModuleNumbers.WorkAndRest) {
        const shouldKeep = this.currentlyAvailableModules.includes(item.moduleNumber);
        return shouldKeep;
      }
      // For all other modules, leave them as is
      return true;
    });
  }
}
